// The WORK-SURFACE component registry — maps a surface id to the React component that renders it. Only the
// five Work surfaces built in this stage have a live component; the rest fall back to the honest scaffold
// placeholder (SurfaceView) until their owning-module read is wired. Keeping the map here (not a switch in
// SurfaceView) keeps the wiring in one place and lets the surface test iterate the built set.
//
// Each component is thin over services: it fetches its typed projection via the injected SurfaceApi and
// renders it natively (tokens only). The map's keys are SurfaceId values (surfaces.ts) — the single source
// for which surfaces exist and which role sees them.

import type { DashboardRole, SurfaceId } from '../app/surfaces.ts';
import { WorkSectionContainer } from './work/WorkSectionContainer.tsx';
import { TodaySurface } from './TodaySurface.tsx';
import { TasksSurface } from './TasksSurface.tsx';
import { KpisSurface } from './KpisSurface.tsx';
import { SchedulerSurface } from './SchedulerSurface.tsx';
import { ApprovalsSurface } from './ApprovalsSurface.tsx';
import { MemorySurface } from './MemorySurface.tsx';
import { FilesSurface } from './FilesSurface.tsx';
import { TeamSurface } from './TeamSurface.tsx';
import { SettingsSurface } from './SettingsSurface.tsx';
import { MonitoringSurface } from './MonitoringSurface.tsx';
import { ConnectorsSurface } from './ConnectorsSurface.tsx';
import { CapabilityMapSurface } from './CapabilityMapSurface.tsx';
import { GovernanceSurface } from './GovernanceSurface.tsx';
import { JourneyLabSurface } from './JourneyLabSurface.tsx';

/**
 * A surface component receives the shell-resolved role (an OPTIONAL prop, so an argument-less component stays
 * assignable — every dashboard surface that fetches its own SurfaceApi projection changes nothing). Only the
 * Work SECTION (`work`) reads it, to render its per-viewer verdict affordances (G-180, rendering-only).
 */
export type SurfaceComponent = (props: { readonly role?: DashboardRole }) => React.JSX.Element;

/** The id → component map for the surfaces with a live render (Work + Knowledge + Operate surfaces built). */
export const WORK_SURFACES: Partial<Readonly<Record<SurfaceId, SurfaceComponent>>> = {
  // The Work section (WS-J) — the shell-mounted in-section router (landing → board → card + operator Composer).
  work: WorkSectionContainer,
  home: TodaySurface,
  tasks: TasksSurface,
  metrics: KpisSurface,
  scheduler: SchedulerSurface,
  approvals: ApprovalsSurface,
  memory: MemorySurface,
  files: FilesSurface,
  team: TeamSurface,
  settings: SettingsSurface,
  // Operate group (Operator-only in the nav; the OWNING read interfaces enforce the data scope, G-180).
  journeys: () => <JourneyLabSurface />,
  monitoring: MonitoringSurface,
  connectors: ConnectorsSurface,
  // CapabilityMapSurface declares its OWN optional props (`onExport`, which the shell does not supply); wrap it
  // to the role-prop component shape so it (like every argument-less surface) is assignable to SurfaceComponent.
  capmap: () => <CapabilityMapSurface />,
  governance: GovernanceSurface,
};

/** Resolve the live component for a surface id, or undefined when only the scaffold placeholder exists. */
export function surfaceComponentFor(id: SurfaceId): SurfaceComponent | undefined {
  return WORK_SURFACES[id];
}
